import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  Tooltip,
  Alert,
  Tabs,
  Tab,
  CircularProgress,
} from '@mui/material';
import {
  AdminPanelSettings as AdminIcon,
  Storage as ServerIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  RestartAlt as RestartIcon,
  Visibility as ViewIcon,
  Build as ToolIcon,
  Description as LogIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import {
  apiService,
  MCPServerStatus,
} from '../services/apiService';

interface User {
  id: string;
  name: string;
  email: string;
  role: 'customer' | 'agent' | 'admin';
}

interface SystemStatus {
  database: boolean;
  mcpServers: boolean;
  aiAgent: boolean;
  llmStatus: boolean;
}

interface AdminPanelProps {
  user: User;
  systemStatus: SystemStatus;
  onNotification: (message: string, severity: 'success' | 'error' | 'warning' | 'info') => void;
}

interface LogEntry {
  timestamp: string;
  level: string;
  message: string;
  meta?: any;
}

const AdminPanel: React.FC<AdminPanelProps> = ({ user, systemStatus, onNotification }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [mcpServers, setMcpServers] = useState<MCPServerStatus[]>([]);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [configDialog, setConfigDialog] = useState<{
    open: boolean;
    serverName: string;
    config: string;
  }>({
    open: false,
    serverName: '',
    config: '',
  });

  useEffect(() => {
    loadAdminData();
  }, []);

  const loadAdminData = async () => {
    try {
      setLoading(true);
      const [mcpStatus, systemLogs] = await Promise.all([
        apiService.getMCPStatus(),
        apiService.getSystemLogs('info', 50),
      ]);
      
      setMcpServers(mcpStatus);
      setLogs(systemLogs.logs);
    } catch (error) {
      console.error('Failed to load admin data:', error);
      onNotification('Failed to load admin data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleRestartServer = async (serverName: string) => {
    try {
      const result = await apiService.restartMCPServer(serverName);
      if (result.success) {
        onNotification(`${serverName} server restarted successfully`, 'success');
        loadAdminData(); // Refresh data
      } else {
        onNotification(result.message || 'Failed to restart server', 'error');
      }
    } catch (error) {
      onNotification('Failed to restart server', 'error');
    }
  };

  const handleConfigSave = async () => {
    try {
      const config = JSON.parse(configDialog.config);
      const result = await apiService.updateMCPServerConfig(configDialog.serverName, config);
      
      if (result.success) {
        onNotification('Configuration updated successfully', 'success');
        setConfigDialog({ open: false, serverName: '', config: '' });
        loadAdminData();
      } else {
        onNotification('Failed to update configuration', 'error');
      }
    } catch (error) {
      onNotification('Invalid JSON configuration', 'error');
    }
  };

  const getStatusIcon = (connected: boolean) => {
    return connected ? (
      <CheckCircleIcon sx={{ color: '#95c100', fontSize: 20 }} />
    ) : (
      <ErrorIcon sx={{ color: '#e74c3c', fontSize: 20 }} />
    );
  };

  const getLogLevelColor = (level: string) => {
    switch (level.toLowerCase()) {
      case 'error': return '#e74c3c';
      case 'warn': return '#95c100';
      case 'info': return '#1fb8cc';
      case 'debug': return '#9e9e9e';
      default: return '#666';
    }
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  const renderMCPServersTab = () => (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          MCP Server Management
        </Typography>
        <Button
          startIcon={<RefreshIcon />}
          onClick={loadAdminData}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Server Name</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Tools</TableCell>
              <TableCell>Resources</TableCell>
              <TableCell>Prompts</TableCell>
              <TableCell>Last Seen</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {mcpServers.map((server) => (
              <TableRow key={server.name}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ width: 32, height: 32, backgroundColor: '#1fb8cc' }}>
                      <ServerIcon sx={{ fontSize: 16 }} />
                    </Avatar>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {server.name}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    icon={getStatusIcon(server.connected)}
                    label={server.connected ? 'Connected' : 'Disconnected'}
                    color={server.connected ? 'success' : 'error'}
                    size="small"
                  />
                </TableCell>
                <TableCell>{server.tools}</TableCell>
                <TableCell>{server.resources}</TableCell>
                <TableCell>{server.prompts}</TableCell>
                <TableCell>
                  <Typography variant="caption" color="textSecondary">
                    {formatTimestamp(server.lastSeen)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="Restart Server">
                      <IconButton
                        size="small"
                        onClick={() => handleRestartServer(server.name)}
                        disabled={!server.connected}
                      >
                        <RestartIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Configure Server">
                      <IconButton
                        size="small"
                        onClick={() => setConfigDialog({
                          open: true,
                          serverName: server.name,
                          config: JSON.stringify({
                            enabled: server.connected,
                            maxConnections: 10,
                            timeout: 30000,
                          }, null, 2),
                        })}
                      >
                        <SettingsIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  const renderSystemStatusTab = () => (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
        System Status Overview
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {getStatusIcon(systemStatus.database)}
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Database
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {systemStatus.database ? 'Connected and operational' : 'Connection failed'}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {getStatusIcon(systemStatus.mcpServers)}
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    MCP Servers
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {systemStatus.mcpServers ? `${mcpServers.filter(s => s.connected).length} servers connected` : 'No servers connected'}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {getStatusIcon(systemStatus.aiAgent)}
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    AI Agent
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {systemStatus.aiAgent ? 'Online and processing' : 'Offline or error'}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="body2">
          All system components must be operational for the AI agent to function properly.
          Check individual MCP servers in the MCP Servers tab for detailed status.
        </Typography>
      </Alert>
    </Box>
  );

  const renderSystemLogsTab = () => (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          System Logs
        </Typography>
        <Button
          startIcon={<RefreshIcon />}
          onClick={loadAdminData}
          disabled={loading}
        >
          Refresh Logs
        </Button>
      </Box>

      <Card>
        <CardContent sx={{ p: 0 }}>
          <List sx={{ maxHeight: 600, overflow: 'auto' }}>
            {logs.map((log, index) => (
              <ListItem key={index} divider>
                <ListItemIcon>
                  <Avatar
                    sx={{
                      width: 24,
                      height: 24,
                      backgroundColor: getLogLevelColor(log.level),
                      fontSize: '0.7rem',
                    }}
                  >
                    {log.level.charAt(0).toUpperCase()}
                  </Avatar>
                </ListItemIcon>
                <ListItemText
                  primary={log.message}
                  secondary={
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                      <Typography variant="caption" color="textSecondary">
                        {formatTimestamp(log.timestamp)}
                      </Typography>
                      <Chip
                        label={log.level.toUpperCase()}
                        size="small"
                        sx={{
                          backgroundColor: getLogLevelColor(log.level),
                          color: 'white',
                          fontSize: '0.6rem',
                          height: 16,
                        }}
                      />
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>
    </Box>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Avatar sx={{ backgroundColor: '#1fb8cc', mr: 2 }}>
          <AdminIcon />
        </Avatar>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Admin Panel
          </Typography>
          <Typography variant="body2" color="textSecondary">
            System administration and MCP server management
          </Typography>
        </Box>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
          <Tab label="MCP Servers" />
          <Tab label="System Status" />
          <Tab label="System Logs" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      {activeTab === 0 && renderMCPServersTab()}
      {activeTab === 1 && renderSystemStatusTab()}
      {activeTab === 2 && renderSystemLogsTab()}

      {/* Configuration Dialog */}
      <Dialog
        open={configDialog.open}
        onClose={() => setConfigDialog({ open: false, serverName: '', config: '' })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Configure {configDialog.serverName} Server
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={15}
            label="Server Configuration (JSON)"
            value={configDialog.config}
            onChange={(e) => setConfigDialog(prev => ({ ...prev, config: e.target.value }))}
            sx={{ mt: 2 }}
            helperText="Enter valid JSON configuration for the MCP server"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfigDialog({ open: false, serverName: '', config: '' })}>
            Cancel
          </Button>
          <Button onClick={handleConfigSave} variant="contained">
            Save Configuration
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminPanel; 